import { TableRow } from '@code/innovance-react-ui-kit';
import React, { createContext, FC, useContext, useReducer } from 'react';
import { CardSelectionType, CardUsageType } from 'src/front/api/generated';
import { CustomerTableRow } from 'src/front/pages/applications/card-entry/CustomerSearch';

export enum ACTIONS {
  SEARCH_CUSTOMER,
  SET_CARD_TYPE,
  SET_PRODUCT_OFFER,
  SET_SCORING,
  SET_SELECTED_PRODUCT_OFFER,
  SWITCH_CUSTOMER_TYPE,
  SET_TRANSFERABLE_CUSTOMER_DATA,
  SET_PRODUCT_OFFER_IS_DEBIT,
  CLEAR,
}

type Action = { type: ACTIONS; payload?: any };

interface CustomerSearchValues {
  customerSurname: string;
  customerName: string;
  customerSecondName: string;
  birthDate: Date | null;
  identityDocumentSerialCode: string;
  identityDocumentSerialNumber: string;
}

interface CardEntryState {
  customerId?: string;
  customerNumber?: string;
  customerSearchValues?: CustomerSearchValues;
  isNewCustomer: boolean;
  cardType: CardSelectionType;
  cardUsageType: CardUsageType;
  pageNumber?: number;
  transferableCardCustomerData?: CustomerTableRow;
  isProductOffer: boolean;
  isScoringDone: boolean;
  selectedProductOffer?: TableRow;
  isproductOfferDebit: boolean;
}

type CardEntryDispatch = (action: Action) => void;

const CardEntryStateContext = createContext<CardEntryState | undefined>(undefined);
const CardEntryDispatchContext = createContext<CardEntryDispatch | undefined>(undefined);

const initialValues = {
  isNewCustomer: false,
  isProductOffer: false,
  isScoringDone: false,
  isproductOfferDebit: false,
  selectedProductOffer: undefined,
  cardType: CardSelectionType.DEBIT,
  cardUsageType: CardUsageType.PRIMARY,
};

const cardEntryReducer = (state: CardEntryState, action: Action): CardEntryState => {
  switch (action.type) {
    case ACTIONS.SET_CARD_TYPE:
      return {
        ...state,
        cardType: action.payload,
      };
    case ACTIONS.SET_SELECTED_PRODUCT_OFFER:
      return {
        ...state,
        selectedProductOffer: action.payload,
      };
    case ACTIONS.SET_PRODUCT_OFFER:
      return {
        ...state,
        isProductOffer: action.payload,
      };
    case ACTIONS.SET_SCORING:
      return {
        ...state,
        isScoringDone: action.payload,
      };
    case ACTIONS.SET_TRANSFERABLE_CUSTOMER_DATA:
      return {
        ...state,
        transferableCardCustomerData: action.payload,
      };
    case ACTIONS.SEARCH_CUSTOMER:
      return {
        ...state,
        ...action.payload,
      };
    case ACTIONS.SWITCH_CUSTOMER_TYPE:
      return {
        ...state,
        ...action.payload,
      };
    case ACTIONS.SET_PRODUCT_OFFER_IS_DEBIT:
      return {
        ...state,
        isproductOfferDebit: action.payload,
      };
    case ACTIONS.CLEAR:
      return {
        ...initialValues,
        ...(action.payload.isOnlyCustomerSearch
          ? {
              cardType: state.cardType,
              cardUsageType: state.cardUsageType,
              isProductOffer: state.isProductOffer,
              isScoringDone: state.isScoringDone,
              selectedProductOffer: state.selectedProductOffer,
            }
          : {}),
      };
    default:
      return state;
  }
};

const CardEntryProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(cardEntryReducer, initialValues);

  return (
    <CardEntryStateContext.Provider value={state}>
      <CardEntryDispatchContext.Provider value={dispatch}>
        {children}
      </CardEntryDispatchContext.Provider>
    </CardEntryStateContext.Provider>
  );
};

export const useCustomerSearchState = (): CardEntryState => {
  const context = useContext(CardEntryStateContext);
  if (context === undefined) {
    throw new Error('useCustomerSearchState must be used within a CardEntryProvider');
  }

  return context;
};

type SearchInput = Pick<
  CardEntryState,
  'customerId' | 'customerNumber' | 'customerSearchValues' | 'isNewCustomer' | 'pageNumber'
>;

type SwitchCustomerInput = Pick<CardEntryState, 'customerId' | 'customerNumber' | 'isNewCustomer'>;

export const useCustomerSearchDispatch = (): ((payload: SearchInput) => void) => {
  const dispatch = useContext(CardEntryDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useCustomerSearchDispatch must be used within a CardEntryProvider');
  }

  return (payload: SearchInput) => dispatch({ type: ACTIONS.SEARCH_CUSTOMER, payload });
};

export const useSwitchCustomerType = (): ((payload: SwitchCustomerInput) => void) => {
  const dispatch = useContext(CardEntryDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSwitchCustomerType must be used within a CardEntryProvider');
  }

  return (payload: SearchInput) => dispatch({ type: ACTIONS.SWITCH_CUSTOMER_TYPE, payload });
};

export const useCardTypeState = (): CardSelectionType => {
  const context = useContext(CardEntryStateContext);
  if (context === undefined) {
    throw new Error('useCardTypeState must be used within a CardEntryProvider');
  }

  return context.cardType;
};
export const useProductOfferState = (): boolean => {
  const context = useContext(CardEntryStateContext);
  if (context === undefined) {
    throw new Error('useProductOfferState must be used within a CardEntryProvider');
  }

  return context.isProductOffer;
};
export const useSelectedProductOfferState = (): TableRow | undefined => {
  const context = useContext(CardEntryStateContext);
  if (context === undefined) {
    throw new Error('useSelectedProductOfferState must be used within a CardEntryProvider');
  }

  return context.selectedProductOffer;
};
export const useScoringState = (): boolean => {
  const context = useContext(CardEntryStateContext);
  if (context === undefined) {
    throw new Error('useScoringState must be used within a CardEntryProvider');
  }

  return context.isScoringDone;
};
export const useProductOfferDebitState = (): boolean => {
  const context = useContext(CardEntryStateContext);
  if (context === undefined) {
    throw new Error('useProductOfferDebitState must be used within a CardEntryProvider');
  }

  return context.isproductOfferDebit;
};
export const useProductOfferDebitDispatch = (): ((payload: boolean) => void) => {
  const dispatch = useContext(CardEntryDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useProductOfferDebitDispatch must be used within a CardEntryProvider');
  }

  return (payload: boolean) => dispatch({ type: ACTIONS.SET_PRODUCT_OFFER_IS_DEBIT, payload });
};
export const useCardTypeDispatch = (): ((payload: CardSelectionType) => void) => {
  const dispatch = useContext(CardEntryDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useCardTypeDispatch must be used within a CardEntryProvider');
  }

  return (payload: CardSelectionType) => dispatch({ type: ACTIONS.SET_CARD_TYPE, payload });
};
export const useProductOfferDispatch = (): ((payload: boolean) => void) => {
  const dispatch = useContext(CardEntryDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useProductOfferDispatch must be used within a CardEntryProvider');
  }

  return (payload: boolean) => dispatch({ type: ACTIONS.SET_PRODUCT_OFFER, payload });
};
export const useSelectedProductOfferDispatch = (): ((payload?: TableRow) => void) => {
  const dispatch = useContext(CardEntryDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSelectedProductOfferDispatch must be used within a CardEntryProvider');
  }

  return (payload?: TableRow) => dispatch({ type: ACTIONS.SET_SELECTED_PRODUCT_OFFER, payload });
};
export const useScoringDispatch = (): ((payload: boolean) => void) => {
  const dispatch = useContext(CardEntryDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useScoringDispatch must be used within a CardEntryProvider');
  }

  return (payload: boolean) => dispatch({ type: ACTIONS.SET_SCORING, payload });
};
export const useTransferableCardCustomerDataDispatch = (): ((
  payload?: CustomerTableRow
) => void) => {
  const dispatch = useContext(CardEntryDispatchContext);
  if (dispatch === undefined) {
    throw new Error(
      'useTransferableCardCustomerDataDispatch must be used within a CardEntryProvider'
    );
  }

  return (payload?: CustomerTableRow) =>
    dispatch({ type: ACTIONS.SET_TRANSFERABLE_CUSTOMER_DATA, payload });
};

export const useClearDispatch = (isOnlyCustomerSearch = false): (() => void) => {
  const dispatch = useContext(CardEntryDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useClearDispatch must be used within a CardEntryProvider');
  }

  return () => dispatch({ type: ACTIONS.CLEAR, payload: { isOnlyCustomerSearch } });
};

export default CardEntryProvider;
